import { useQueryClient } from '@tanstack/react-query';
import { Download, FileSpreadsheet, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import type { catalog } from '@terra/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Input,
  PageHeader,
  Spinner,
  type BadgeTone,
} from '@terra/ui';

import { api } from '../lib/api';
import {
  chunk,
  chunkProductRows,
  downloadCsv,
  parseProductSheet,
  parseStockSheet,
  PRODUCT_SAMPLE_ROWS,
  PRODUCT_TEMPLATE_HEADERS,
  type ParsedSheet,
} from '../lib/bulkSheets';

type Status = 'OK' | 'ERROR' | 'SKIPPED';
const TONE: Record<Status, BadgeTone> = { OK: 'success', ERROR: 'danger', SKIPPED: 'warning' };

function apiError(err: unknown): string {
  const e = err as {
    response?: { status?: number; data?: { error?: { message?: string } } };
    message?: string;
  };
  if (e.response?.status === 429) {
    return 'Too many uploads in a short time. Wait a minute, then press the button again — rows already done are kept.';
  }
  return e.response?.data?.error?.message ?? e.message ?? 'The upload did not go through.';
}

function Progress({ done, total, label }: { done: number; total: number; label: string }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="space-y-1.5" role="status">
      <div className="flex justify-between text-xs font-semibold text-slate-600">
        <span>{label}</span>
        <span>
          {done} / {total}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full bg-slate-900 transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function FilePicker({
  onText,
  label,
}: {
  onText: (name: string, text: string) => void;
  label: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onText(file.name, await file.text());
        }}
      />
      <Button onClick={() => ref.current?.click()}>
        <Upload className="h-4 w-4" /> {label}
      </Button>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Products
// ═══════════════════════════════════════════════════════════════════════════

interface ProductRowView {
  line: number;
  sku: string;
  status: Status;
  messages: string[];
  productStatus?: string;
}

function ProductsBulk() {
  const qc = useQueryClient();
  const [fileName, setFileName] = useState<string | null>(null);
  const [sheet, setSheet] = useState<ParsedSheet<catalog.BulkProductRow> | null>(null);
  const [checked, setChecked] = useState<Map<number, ProductRowView> | null>(null);
  const [imported, setImported] = useState(false);
  const [busy, setBusy] = useState<{ label: string; done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitForApproval, setSubmitForApproval] = useState(true);

  const load = (name: string, text: string) => {
    setFileName(name);
    setSheet(parseProductSheet(text));
    setChecked(null);
    setImported(false);
    setError(null);
  };

  const sheetIssues = (): ProductRowView[] =>
    (sheet?.issues ?? []).reduce<ProductRowView[]>((out, i) => {
      const existing = out.find((r) => r.line === i.line);
      if (existing) existing.messages.push(i.message);
      else out.push({ line: i.line, sku: '', status: 'ERROR', messages: [i.message] });
      return out;
    }, []);

  const toView = (r: catalog.BulkProductRowResult): ProductRowView => ({
    line: r.line,
    sku: r.sku,
    status: r.status,
    messages: [...r.errors, ...r.warnings],
    ...(r.productStatus ? { productStatus: r.productStatus } : {}),
  });

  async function run(dryRun: boolean, rows: catalog.BulkProductRow[]) {
    const chunks = chunkProductRows(rows);
    const results = new Map<number, ProductRowView>();
    setError(null);
    setBusy({ label: dryRun ? 'Checking rows' : 'Creating products', done: 0, total: rows.length });
    let done = 0;
    try {
      for (const c of chunks) {
        const res = await api.products.bulkCreateProducts({ dryRun, submitForApproval, rows: c });
        for (const r of res.rows) results.set(r.line, toView(r));
        done += c.length;
        setBusy({
          label: dryRun ? 'Checking rows' : 'Creating products',
          done,
          total: rows.length,
        });
      }
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(null);
    }
    return results;
  }

  const check = async () => {
    if (!sheet) return;
    setChecked(await run(true, sheet.rows));
  };

  /** Only products whose every row passed the check are sent. */
  const readyRows = () => {
    if (!sheet || !checked) return [];
    const badHandles = new Set(
      sheet.rows
        .filter((r) => checked.get(r.line)?.status !== 'OK')
        .map((r) => r.handle?.trim().toLowerCase())
        .filter(Boolean),
    );
    return sheet.rows.filter(
      (r) =>
        checked.get(r.line)?.status === 'OK' &&
        !(r.handle && badHandles.has(r.handle.trim().toLowerCase())),
    );
  };

  const importNow = async () => {
    const rows = readyRows();
    if (rows.length === 0) return;
    const results = await run(false, rows);
    setChecked((prev) => {
      const merged = new Map(prev ?? []);
      for (const [line, v] of results) merged.set(line, v);
      return merged;
    });
    setImported(true);
    void qc.invalidateQueries({ queryKey: ['vendor-products'] });
    void qc.invalidateQueries({ queryKey: ['vendor-inventory'] });
    void qc.invalidateQueries({ queryKey: ['vendor-dashboard-summary'] });
  };

  const views: ProductRowView[] = [
    ...sheetIssues(),
    ...(checked ? [...checked.values()] : []),
  ].sort((a, b) => a.line - b.line);
  const ready = readyRows();
  const readyProducts = new Set(ready.map((r) => r.handle?.trim().toLowerCase() || `#${r.line}`))
    .size;
  const problemCount = views.filter((v) => v.status !== 'OK').length;

  const downloadCategories = async () => {
    const cats = await api.categories.list({ active: true });
    const byId = new Map(cats.map((c) => [c.id, c]));
    downloadCsv('categories.csv', [
      ['name', 'slug', 'parent'],
      ...cats
        .map((c) => [c.name, c.slug, c.parentId ? (byId.get(c.parentId)?.name ?? '') : ''])
        .sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
    ]);
  };

  return (
    <div className="space-y-5">
      <Card>
        <CardBody className="space-y-4">
          <h2 className="text-base font-bold text-slate-900">1. Fill in the sheet</h2>
          <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600">
            <li>
              One row per product. For sizes or colours, give the rows the same <b>handle</b> and
              fill <b>options</b> like <code>Size=M|Colour=Red</code>; name, category and HSN are
              read from the first row of the handle.
            </li>
            <li>
              <b>category</b> must be one that already exists — download the category list.{' '}
              <b>sku</b> must be unique; a SKU already in your catalogue is never duplicated.
            </li>
            <li>
              Prices in rupees (<code>499</code> or <code>499.50</code>). <b>stock</b> is the
              opening quantity in your default warehouse. <b>image_urls</b>: up to 4 public https
              links separated by <code>|</code>.
            </li>
          </ul>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadCsv('products-template.csv', [PRODUCT_TEMPLATE_HEADERS])}
            >
              <Download className="h-4 w-4" /> Blank template
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                downloadCsv('products-sample.csv', [
                  PRODUCT_TEMPLATE_HEADERS,
                  ...PRODUCT_SAMPLE_ROWS,
                ])
              }
            >
              <FileSpreadsheet className="h-4 w-4" /> Sample filled in
            </Button>
            <Button variant="outline" size="sm" onClick={() => void downloadCategories()}>
              <Download className="h-4 w-4" /> Category list
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-4">
          <h2 className="text-base font-bold text-slate-900">2. Upload and check</h2>
          <div className="flex flex-wrap items-center gap-3">
            <FilePicker
              onText={load}
              label={fileName ? 'Choose another file' : 'Choose CSV file'}
            />
            {fileName && <span className="text-sm font-medium text-slate-600">{fileName}</span>}
          </div>
          {sheet?.fatal && (
            <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{sheet.fatal}</p>
          )}
          {sheet && !sheet.fatal && (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-slate-600">
                {sheet.rows.length} row{sheet.rows.length === 1 ? '' : 's'} ready to check
                {sheet.issues.length > 0 &&
                  `, ${sheet.issues.length} problem(s) in the file itself`}
              </span>
              <Button
                variant="secondary"
                disabled={!!busy || sheet.rows.length === 0 || imported}
                onClick={() => void check()}
              >
                Check rows
              </Button>
            </div>
          )}
          {busy && <Progress {...busy} />}
          {error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
        </CardBody>
      </Card>

      {checked && !imported && (
        <Card>
          <CardBody className="space-y-3">
            <h2 className="text-base font-bold text-slate-900">3. Import</h2>
            <p className="text-sm text-slate-600">
              {readyProducts} product{readyProducts === 1 ? '' : 's'} ready
              {problemCount > 0 &&
                ` · ${problemCount} row(s) need fixing and will be left out — fix them and upload again later`}
            </p>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={submitForApproval}
                onChange={(e) => setSubmitForApproval(e.target.checked)}
              />
              Send for approval straight away (otherwise they stay as drafts)
            </label>
            <Button disabled={!!busy || readyProducts === 0} onClick={() => void importNow()}>
              {busy ? <Spinner size="sm" className="text-white" /> : null}
              Import {readyProducts} product{readyProducts === 1 ? '' : 's'}
            </Button>
          </CardBody>
        </Card>
      )}

      {imported && (
        <p className="rounded-lg bg-emerald-50 p-3 text-sm font-semibold text-emerald-800">
          Done. Created products are on your{' '}
          <Link to="/products" className="underline">
            Products
          </Link>{' '}
          page. Rows marked in red were not imported.
        </p>
      )}

      {views.length > 0 && (
        <ResultsTable
          onDownload={() =>
            downloadCsv('products-upload-results.csv', [
              ['line', 'sku', 'status', 'product_status', 'messages'],
              ...views.map((v) => [
                v.line,
                v.sku,
                v.status,
                v.productStatus ?? '',
                v.messages.join(' | '),
              ]),
            ])
          }
          head={['Line', 'SKU', 'Listing', 'Details']}
          rows={views.map((v) => ({
            key: v.line,
            status: v.status,
            cells: [
              v.line,
              v.sku || '—',
              v.productStatus ? v.productStatus.replace(/_/g, ' ').toLowerCase() : '—',
              v.messages.join(' · ') || (imported ? 'Created' : 'Ready'),
            ],
          }))}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Stock
// ═══════════════════════════════════════════════════════════════════════════

function StockBulk() {
  const qc = useQueryClient();
  const [fileName, setFileName] = useState<string | null>(null);
  const [sheet, setSheet] = useState<ParsedSheet<catalog.BulkStockRow> | null>(null);
  const [preview, setPreview] = useState<catalog.BulkStockRowResult[] | null>(null);
  const [applied, setApplied] = useState<catalog.BulkStockResponse | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<{ label: string; done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = (name: string, text: string) => {
    setFileName(name);
    setSheet(parseStockSheet(text));
    setPreview(null);
    setApplied(null);
    setError(null);
  };

  async function run(dryRun: boolean, rows: catalog.BulkStockRow[]) {
    const out: catalog.BulkStockRowResult[] = [];
    const totals = { updated: 0, unchanged: 0, failed: 0 };
    setError(null);
    const label = dryRun ? 'Checking stock' : 'Updating stock';
    setBusy({ label, done: 0, total: rows.length });
    let done = 0;
    try {
      for (const c of chunk(rows, 1000)) {
        const res = await api.products.bulkSetStock({
          dryRun,
          rows: c,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        });
        out.push(...res.rows);
        totals.updated += res.updated;
        totals.unchanged += res.unchanged;
        totals.failed += res.failed;
        done += c.length;
        setBusy({ label, done, total: rows.length });
      }
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(null);
    }
    return { rows: out, ...totals, dryRun };
  }

  const downloadCurrent = async () => {
    const [inventory, warehouses] = await Promise.all([
      api.products.getVendorInventory(),
      api.vendors.listMyWarehouses(),
    ]);
    const fallback = warehouses[0]?.name ?? '';
    const lines: unknown[][] = [['sku', 'product', 'warehouse', 'quantity']];
    for (const v of inventory as any[]) {
      const items: any[] = v.inventoryItems ?? [];
      if (items.length === 0) lines.push([v.sku, v.product?.title ?? '', fallback, 0]);
      for (const i of items)
        lines.push([v.sku, v.product?.title ?? '', i.warehouse?.name ?? fallback, i.onHand]);
    }
    downloadCsv(`stock-${new Date().toISOString().slice(0, 10)}.csv`, lines);
  };

  const okRows = preview ? preview.filter((r) => r.status === 'OK').map((r) => r.line) : [];
  const changes = preview
    ? preview.filter((r) => r.status === 'OK' && r.onHandBefore !== r.onHandAfter).length
    : 0;

  const apply = async () => {
    if (!sheet) return;
    const rows = sheet.rows.filter((r) => okRows.includes(r.line));
    const res = await run(false, rows);
    setApplied(res);
    void qc.invalidateQueries({ queryKey: ['vendor-inventory'] });
    void qc.invalidateQueries({ queryKey: ['vendor-warehouses'] });
  };

  const shown: catalog.BulkStockRowResult[] = [
    ...(sheet?.issues ?? []).map((i) => ({
      line: i.line,
      sku: '',
      warehouse: null,
      status: 'ERROR' as const,
      errors: [i.message],
    })),
    ...(applied?.rows ?? preview ?? []),
  ].sort((a, b) => a.line - b.line);

  return (
    <div className="space-y-5">
      <Card>
        <CardBody className="space-y-3">
          <h2 className="text-base font-bold text-slate-900">1. Get your stock sheet</h2>
          <p className="text-sm text-slate-600">
            Download your current stock, change the <b>quantity</b> column to what is on the shelf
            now, and upload it back. Quantity is the new level, not a change. Leave <b>warehouse</b>{' '}
            blank for your default warehouse. The product column is only there to help you and is
            not read.
          </p>
          <Button variant="outline" size="sm" onClick={() => void downloadCurrent()}>
            <Download className="h-4 w-4" /> Download current stock
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-4">
          <h2 className="text-base font-bold text-slate-900">2. Upload, check, apply</h2>
          <div className="flex flex-wrap items-center gap-3">
            <FilePicker
              onText={load}
              label={fileName ? 'Choose another file' : 'Choose CSV file'}
            />
            {fileName && <span className="text-sm font-medium text-slate-600">{fileName}</span>}
          </div>
          <div className="max-w-md">
            <Input
              label="Reason (saved in the stock history)"
              placeholder="e.g. Monthly stock count"
              value={reason}
              maxLength={200}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          {sheet?.fatal && (
            <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{sheet.fatal}</p>
          )}
          {sheet && !sheet.fatal && (
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="secondary"
                disabled={!!busy || sheet.rows.length === 0 || !!applied}
                onClick={async () => setPreview((await run(true, sheet.rows)).rows)}
              >
                Check {sheet.rows.length} row{sheet.rows.length === 1 ? '' : 's'}
              </Button>
              {preview && !applied && (
                <Button disabled={!!busy || changes === 0} onClick={() => void apply()}>
                  Apply {changes} change{changes === 1 ? '' : 's'}
                </Button>
              )}
            </div>
          )}
          {busy && <Progress {...busy} />}
          {error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          {applied && (
            <p className="rounded-lg bg-emerald-50 p-3 text-sm font-semibold text-emerald-800">
              {applied.updated} updated · {applied.unchanged} already correct · {applied.failed} not
              changed because of a problem.
            </p>
          )}
        </CardBody>
      </Card>

      {shown.length > 0 && (
        <ResultsTable
          onDownload={() =>
            downloadCsv('stock-upload-results.csv', [
              ['line', 'sku', 'warehouse', 'before', 'after', 'reserved', 'status', 'messages'],
              ...shown.map((r) => [
                r.line,
                r.sku,
                r.warehouse ?? '',
                r.onHandBefore ?? '',
                r.onHandAfter ?? '',
                r.reserved ?? '',
                r.status,
                r.errors.join(' | '),
              ]),
            ])
          }
          head={['Line', 'SKU', 'Warehouse', 'Stock', 'Details']}
          rows={shown.map((r) => ({
            key: r.line,
            status: r.status,
            cells: [
              r.line,
              r.sku,
              r.warehouse ?? '—',
              r.onHandBefore === undefined
                ? '—'
                : r.onHandAfter === undefined || r.onHandAfter === r.onHandBefore
                  ? `${r.onHandBefore}`
                  : `${r.onHandBefore} → ${r.onHandAfter}`,
              r.errors.join(' · ') || (r.onHandAfter === r.onHandBefore ? 'No change' : 'OK'),
            ],
          }))}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════

function ResultsTable({
  head,
  rows,
  onDownload,
}: {
  head: string[];
  rows: Array<{ key: number; status: Status; cells: Array<string | number> }>;
  onDownload: () => void;
}) {
  const problems = rows.filter((r) => r.status !== 'OK').length;
  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-bold text-slate-900">
            Results{' '}
            <span className="text-sm font-medium text-slate-500">
              {rows.length - problems} OK · {problems} with problems
            </span>
          </h2>
          <Button variant="outline" size="sm" onClick={onDownload}>
            <Download className="h-4 w-4" /> Download results
          </Button>
        </div>
        <div className="max-h-[480px] overflow-auto rounded-lg border border-slate-200">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 font-semibold">Status</th>
                {head.map((h) => (
                  <th key={h} className="px-3 py-2 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.key} className="align-top">
                  <td className="px-3 py-2">
                    <Badge tone={TONE[r.status]}>{r.status}</Badge>
                  </td>
                  {r.cells.map((c, i) => (
                    <td
                      key={i}
                      className={`px-3 py-2 ${
                        i === r.cells.length - 1 && r.status !== 'OK'
                          ? 'text-red-700'
                          : 'text-slate-700'
                      }`}
                    >
                      {c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

export function BulkUploadPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'stock' ? 'stock' : 'products';
  return (
    <div className="space-y-5">
      <PageHeader
        title="Bulk upload"
        description="Add many products, or update stock for many SKUs, from one spreadsheet. Every row is checked before anything is saved."
      />
      <div className="flex gap-4 border-b border-slate-200">
        {(
          [
            ['products', 'Products'],
            ['stock', 'Stock'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setParams(id === 'products' ? {} : { tab: id })}
            className={`-mb-px border-b-2 pb-2 text-sm font-semibold ${
              tab === id ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'products' ? <ProductsBulk /> : <StockBulk />}
    </div>
  );
}
